import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDistance, getRhumbLineBearing } from 'geolib';
import * as Selectors from '../../redux/selectors';
import setEmployeeLocation from '../../redux/actions/setEmployeeLocation';
import { getAddessWithCoordinates, isValidCoordinates } from '../../utils/utils';
import PropTypes from 'prop-types';

export const ServiceWorkerGeoLocationContext = React.createContext();

const ACCURATE_RADIO_METERS = 10; // 10 meters
const THROTTLE_TIME = inMilliseconds(2.5); // 2.5 seconds
const GPS_TRACKING_INTERVAL = inMilliseconds(25); // 25 seconds

/**
 * Calculate accurate distance using geolib
 */
const calculateDistance = (pos1, pos2) => {
  return getDistance(pos1, pos2, 1);
};

/**
 * Calculate bearing between two points using geolib
 */
const calculateBearing = (pos1, pos2) => {
  try {
    return getRhumbLineBearing(pos1, pos2); // Returns bearing in degrees
  } catch (error) {
    console.error('Bearing calculation failed:', error);
    return null;
  }
};

/**
 * Enhanced filtering logic with accuracy consideration using Google Maps geometry
 */
const shouldUpdateLocation = (previousPos, newCoords) => {
  if (!previousPos) {
    return { shouldUpdate: true, reason: 'first_position' };
  }

  const distance = calculateDistance(previousPos, newCoords);
  
  // Dynamic threshold based on GPS accuracy
  const accuracyThreshold = Math.max(
    newCoords.accuracy || 50,
    ACCURATE_RADIO_METERS
  );

  if (distance > accuracyThreshold) {
    return { 
      shouldUpdate: true, 
      reason: 'significant_movement',
      distance: Math.round(distance),
      threshold: Math.round(accuracyThreshold)
    };
  }
  
  // Check if accuracy has significantly improved
  const previousAccuracy = previousPos.accuracy || 100;
  if (
    newCoords.accuracy &&
    newCoords.accuracy < previousAccuracy * 0.5 &&
    newCoords.accuracy < 15
  ) {
    return { 
      shouldUpdate: true, 
      reason: 'accuracy_improvement',
      oldAccuracy: Math.round(previousAccuracy),
      newAccuracy: Math.round(newCoords.accuracy)
    };
  }
  
  return { 
    shouldUpdate: false, 
    reason: 'within_threshold',
    distance: Math.round(distance),
    threshold: Math.round(accuracyThreshold)
  };
};

/**
 * @param {Number} secs - The seconds to convert to milliseconds
 * @returns The seconds converted to milliseconds
 */
function inMilliseconds(secs) {
  return secs * 1000;
}

export const ServiceWorkerGeoLocationProvider = ({ children }) => {
  const user = useSelector(Selectors.selectUser);
  const dispatch = useDispatch();
  
  // State to match your existing provider API
  const [coords, setCoords] = useState(null);
  const [isGeolocationAvailable, setIsGeolocationAvailable] = useState(false);
  const [isGeolocationEnabled, setIsGeolocationEnabled] = useState(false);
  const [positionError, setPositionError] = useState(null);
  const [gpsCaptureStatus, setGpsCaptureStatus] = useState('Initializing GPS...');
  
  // Refs for tracking
  const previousPosition = useRef(null);
  const lastUpdate = useRef(null);
  const serviceWorkerRef = useRef(null);
  
  // Stored addresses cache
  const [storedAddresses, setStoredAddresses] = useState(() => 
    localStorage.getItem('storedAddesses')
      ? JSON.parse(localStorage.getItem('storedAddesses'))
      : []
  );

  /**
   * Initialize Service Worker GPS tracking
   */
  const initializeServiceWorker = useCallback(async () => {
    console.log('🚀 Initializing Service Worker GPS...');
    
    if (!('serviceWorker' in navigator)) {
      setGpsCaptureStatus('Service Worker not supported, GPS unavailable');
      setIsGeolocationAvailable(false);
      return;
    }

    // Check geolocation availability first
    if (!('geolocation' in navigator)) {
      setGpsCaptureStatus('Geolocation not supported by this browser');
      setIsGeolocationAvailable(false);
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      serviceWorkerRef.current = registration;
      
      // Test geolocation availability by requesting permission
      setGpsCaptureStatus('Checking GPS permissions...');
      
      // Check for permissions API
      if ('permissions' in navigator) {
        try {
          const permission = await navigator.permissions.query({ name: 'geolocation' });
          console.log('📍 Geolocation permission status:', permission.state);
          
          if (permission.state === 'granted') {
            setIsGeolocationAvailable(true);
            setIsGeolocationEnabled(true);
            setGpsCaptureStatus('GPS permissions granted, initializing...');
          } else if (permission.state === 'prompt') {
            // Request permission by attempting to get location
            requestInitialLocation();
          } else {
            setIsGeolocationAvailable(false);
            setIsGeolocationEnabled(false);
            setGpsCaptureStatus('GPS permissions denied. Please enable location access.');
            return;
          }
        } catch (permError) {
          console.error('Permissions API not fully supported, testing geolocation directly');
          requestInitialLocation();
        }
      } else {
        // No permissions API, test geolocation directly
        requestInitialLocation();
      }

    } catch (error) {
      console.error('Failed to initialize Service Worker GPS:', error);
      setGpsCaptureStatus(`GPS initialization failed: ${error.message}`);
      setIsGeolocationAvailable(false);
    }
  }, []);

  /**
   * Request initial location to test geolocation availability
   */
  const requestInitialLocation = useCallback(() => {
    setGpsCaptureStatus('Testing GPS availability...');
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('✅ Initial GPS test successful:', position);
        setIsGeolocationAvailable(true);
        setIsGeolocationEnabled(true);
        setGpsCaptureStatus('GPS available, starting tracking...');
        
        // Now start GPS tracking
        startGPSTracking();
      },
      (error) => {
        console.error('❌ Initial GPS test failed:', error);
        setIsGeolocationAvailable(false);
        setIsGeolocationEnabled(false);
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setGpsCaptureStatus('GPS permission denied by user');
            break;
          case error.POSITION_UNAVAILABLE:
            setGpsCaptureStatus('GPS position unavailable');
            break;
          case error.TIMEOUT:
            setGpsCaptureStatus('GPS request timeout - retrying...');
            // Retry with less strict settings
            retryLocationRequest();
            break;
          default:
            setGpsCaptureStatus(`GPS error: ${error.message}`);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }, []);

  /**
   * Retry location request with relaxed settings
   */
  const retryLocationRequest = useCallback(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('✅ GPS retry successful:', position);
        setIsGeolocationAvailable(true);
        setIsGeolocationEnabled(true);
        setGpsCaptureStatus('GPS available (low accuracy mode), starting tracking...');
        startGPSTracking();
      },
      (error) => {
        console.error('❌ GPS retry also failed:', error);
        setIsGeolocationAvailable(false);
        setIsGeolocationEnabled(false);
        setGpsCaptureStatus(`GPS unavailable: ${error.message}`);
      },
      {
        enableHighAccuracy: false, // Relaxed accuracy
        timeout: 15000,           // Longer timeout
        maximumAge: 60000        // Accept older positions
      }
    );
  }, []);


  // Add this new function to detect building exits:
  const detectBuildingExit = (previousPos, newCoords) => {
    if (!previousPos) return false;
    
    const accuracyImprovement = (previousPos.accuracy || 100) - (newCoords.accuracy || 50);
    const distance = calculateDistance(previousPos, newCoords);
    
    // Building exit indicators:
    // 1. Accuracy improved significantly (GPS signal got better)
    // 2. Small but consistent movement
    // 3. Previous location had poor accuracy (was indoors)
    
    const hadPoorAccuracy = (previousPos.accuracy || 100) > 40;
    const nowHasGoodAccuracy = (newCoords.accuracy || 50) < 20;
    const significantAccuracyImprovement = accuracyImprovement > 20;
    const minimalMovement = distance > 3 && distance < 50;
    
    return hadPoorAccuracy && 
          nowHasGoodAccuracy && 
          significantAccuracyImprovement && 
          minimalMovement;
  };

  /**
   * Handle position updates from service worker with Google Maps enhanced filtering
   */
  const handlePositionUpdate = useCallback((positionData) => {
    const { latitude, longitude, accuracy, timestamp } = positionData;
    
    const newCoords = {
      latitude,
      longitude,
      accuracy,
      timestamp
    };

    setCoords(newCoords);
    setPositionError(null);
    setIsGeolocationEnabled(true);

    // Check for building exit first
    const isBuildingExit = detectBuildingExit(previousPosition.current, newCoords);
    
    let filterResult;
    if (isBuildingExit) {
      filterResult = {
        shouldUpdate: true,
        reason: 'building_exit_detected',
        distance: Math.round(calculateDistance(previousPosition.current, newCoords)),
        accuracyImprovement: Math.round((previousPosition.current?.accuracy || 100) - accuracy)
      };
    } else {
      filterResult = shouldUpdateLocation(previousPosition.current, newCoords);
    }
    
    // Calculate bearing if we have previous position
    let bearingInfo = '';
    if (previousPosition.current && filterResult.shouldUpdate) {
      const bearing = calculateBearing(previousPosition.current, newCoords);
      if (bearing !== null) {
        const direction = getDirectionFromBearing(bearing);
        bearingInfo = ` | Moving ${direction}`;
      }
    }
    
    setGpsCaptureStatus(
      `GPS active - Accuracy: ${Math.round(accuracy)}m | ${filterResult.reason.replace('_', ' ')}${bearingInfo}`
    );

    // Process the location update with enhanced logic
    processLocationUpdate(newCoords, filterResult);
  }, []);

  /**
   * Get cardinal direction from bearing
   */
  const getDirectionFromBearing = (bearing) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(((bearing + 360) % 360) / 45) % 8;
    return directions[index];
  };

  /**
   * Handle position errors from service worker
   */
  const handlePositionError = useCallback((error) => {
    setPositionError({ message: error });
    setIsGeolocationEnabled(false);
    setGpsCaptureStatus(`GPS Error: ${error}`);
  }, []);

  /**
   * Process location updates with Google Maps enhanced distance filtering
   */
  const processLocationUpdate = useCallback(async (newCoords, filterResult) => {
    const { latitude, longitude, accuracy } = newCoords;
    let isRefresh = false;

    if (filterResult.shouldUpdate) {
      // Significant movement or accuracy improvement
      previousPosition.current = { 
        latitude, 
        longitude, 
        accuracy,
        timestamp: newCoords.timestamp
      };
      
      console.log(`📍 Location update: ${filterResult.reason}`, {
        distance: filterResult.distance,
        threshold: filterResult.threshold,
        accuracy: Math.round(accuracy),
        provider: 'Service worker tracking'
      });
    } else {
      // Minor movement, just refresh without API call
      isRefresh = true;
      console.log(`🔄 Location refresh (${filterResult.reason})`, {
        distance: filterResult.distance,
        threshold: filterResult.threshold,
        provider: 'Service worker tracking'
      });
    }

    try {
      const address = await saveLocation(dispatch, user, newCoords, true, isRefresh);
      if (address) {
        saveAddress(address, newCoords);
      }
    } catch (error) {
      console.error('Failed to save location:', error);
    }
  }, [dispatch, user, storedAddresses]);

  /**
   * Start GPS tracking via service worker
   */
  const startGPSTracking = useCallback(() => {
    if (serviceWorkerRef.current) {
      serviceWorkerRef.current.active.postMessage({
        type: 'START_GPS_TRACKING',
        payload: {
          trackingInterval: GPS_TRACKING_INTERVAL,
          enableHighAccuracy: true,
          timeout: inMilliseconds(30),
          maximumAge: inMilliseconds(30)
        }
      });
      setGpsCaptureStatus('Starting enhanced GPS tracking.');
    } else {
      console.error('Service Worker not ready for GPS tracking');
    }
  }, []);

  /**
   * Save location to Redux and API with enhanced throttling
   */
  const saveLocation = async (dispatch, user, coords, isGpsEnabled, isUpdate = false) => {
    let formattedAddress = user.formattedAddress || '';
    const { latitude, longitude } = coords;

    if (isValidCoordinates([latitude, longitude])) {
      const storedAddress = searchAddress(coords);
      if (storedAddress) {
        formattedAddress = storedAddress;
      } else {
        formattedAddress = await getAddessWithCoordinates(latitude, longitude);
      }
      isGpsEnabled = true;
    } else {
      isGpsEnabled = false;
    }

    const locationData = {
      employeeId: user.customerId,
      customerId: user.ownerId ? user.ownerId : user.customerId,
      address: {
        type: 'Point',
        coordinates: [latitude, longitude],
      },
      formattedAddress,
      gpsEnabled: isGpsEnabled,
    };

    // Enhanced throttling - only for non-refresh updates
    if (!isUpdate && lastUpdate.current && new Date() - lastUpdate.current < THROTTLE_TIME) {
      console.log('⏱️ API call throttled');
      return locationData.formattedAddress;
    }
    
    if (!isUpdate) {
      lastUpdate.current = new Date();
    }

    dispatch(setEmployeeLocation(locationData, isUpdate));
    return locationData.formattedAddress;
  };

  /**
   * Search for cached address with higher precision
   */
  const searchAddress = (coordinates) => {
    const { latitude: lat, longitude: lng } = coordinates;
    // Use 5 decimal places for better accuracy (~1.1m precision)
    const address = storedAddresses.find(
      (item) => item.lat === lat.toFixed(5) && item.lng === lng.toFixed(5)
    );
    return address ? address.address : null;
  };

  /**
   * Save address to cache with enhanced data
   */
  const saveAddress = (address, coords) => {
    if (address && coords) {
      const { latitude: lat, longitude: lng, accuracy } = coords;
      const addressExists = searchAddress(coords);
      if (!addressExists) {
        const newAddress = {
          lat: lat.toFixed(5), // Higher precision
          lng: lng.toFixed(5), // Higher precision
          address,
          coords: {
            latitude: lat,
            longitude: lng
          },
          accuracy: accuracy,
          timestamp: coords.timestamp
        };
        setStoredAddresses(prev => [...prev, newAddress]);
        console.log('💾 Address cached:', address);
      }
    }
  };

  // Handle user data request from service worker - SINGLE DECLARATION
  const handleUserDataRequest = useCallback((gpsData) => {
    if (serviceWorkerRef.current?.active) {
      serviceWorkerRef.current.active.postMessage({
        type: 'GPS_USER_DATA_RESPONSE',
        userData: {
          customerId: user.customerId,
          ownerId: user.ownerId ? user.ownerId : user.customerId,
          token: user.token
        },
        locationData: gpsData
      });
    }
  }, [user]);

  /**
   * Handle background sync request from service worker
   */
  const handleBackgroundSyncRequest = useCallback(async (location) => {
    if (serviceWorkerRef.current && user) {
      // Process background sync for stored location
      try {
        const address = await getAddessWithCoordinates(location.latitude, location.longitude);
        
        const userData = {
          customerId: user.customerId,
          ownerId: user.ownerId,
          formattedAddress: address,
          token: user.token,
        };

        serviceWorkerRef.current.active.postMessage({
          type: 'GPS_BACKGROUND_SYNC_RESPONSE',
          userData,
          location
        });
      } catch (error) {
        console.error('Failed to process background sync:', error);
      }
    }
  }, [user]);

  // Initialize on mount - separate from user dependency
  useEffect(() => {
    initializeServiceWorker();
  }, [initializeServiceWorker]);

  // Listen for service worker messages - separate useEffect
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      // Validate event data structure
      if (!event.data || !event.data.type) {
        console.warn('⚠️ Invalid service worker message:', event.data);
        return;
      }

      console.log('📨 Service worker message:', event.data.type);

      switch (event.data.type) {
        case 'SW_REQUEST_GPS_POSITION':
          // Service worker is requesting GPS position
          handleGPSRequestFromServiceWorker(event.data.config || {});
          break;
          
        case 'GPS_REQUEST_USER_DATA':
          // Service worker needs user data to sync with GPS
          if (event.data.gpsData && validateGPSData(event.data.gpsData)) {
            handleUserDataRequest(event.data.gpsData);
          } else {
            console.error('❌ Invalid GPS data received from service worker:', event.data.gpsData);
          }
          break;
          
        case 'GPS_POSITION_UPDATE':
          // Handle GPS position update from service worker
          if (event.data.locationData && validateGPSData(event.data.locationData)) {
            handlePositionUpdate(event.data.locationData);
          } else {
            console.error('❌ Invalid location data in GPS position update:', event.data.locationData);
          }
          break;

        case 'GPS_ERROR':
          // Handle GPS errors from service worker
          handlePositionError(event.data.error);
          break;
          
        case 'LOCATION_RETRY_SUCCESS': {
          // Wrap in block scope when declaring variables
          const address = event.data.locationData?.address || 'Location updated';
          console.log('✅ Service worker successfully retried location sync');
          setGpsCaptureStatus(`✅ Offline location synced: ${address}`);
          break;
        }

        case 'CLIENT_CONNECTION_TEST_RESPONSE':
          console.log('✅ Service worker connection test successful:', event.data.message);
          setGpsCaptureStatus('Service worker connected and ready');
          break;

        case 'sync-report-done': {
          const reportId = event.data.reportId;
          console.log('✅ Report sync completed:', reportId);
          break;
        }

        case 'GPS_USER_DATA_ERROR': {
          const errorMessage = event.data.error?.message || 'User data error';
          console.error('❌ GPS user data error from service worker:', event.data.error);
          setGpsCaptureStatus(`GPS Error: ${errorMessage}`);
          break;
        }

        case 'GPS_BACKGROUND_SYNC_RESPONSE':
          console.log('✅ GPS background sync response received');
          break;

        case 'LOCATION_SYNC_SUCCESS': {
          const locationData = event.data.locationData;
          console.log('✅ Location sync successful:', locationData);
          break;
        }

        case 'LOCATION_SYNC_FAILED': {
          const failedLocation = event.data.locationData;
          console.error('❌ Location sync failed:', failedLocation);
          setGpsCaptureStatus('❌ Location sync failed - will retry offline');
          break;
        }

        default:
          console.log('📨 Unhandled service worker message type:', event.data.type);
          break;
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, [handleGPSRequestFromServiceWorker, handleUserDataRequest, validateGPSData, handlePositionUpdate]);

  // Update stored addresses in localStorage
  useEffect(() => {
    localStorage.setItem('storedAddesses', JSON.stringify(storedAddresses));
  }, [storedAddresses]);

  // Wrap contextValue in useMemo to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      coords,
      isGeolocationAvailable,
      isGeolocationEnabled,
      positionError,
      gpsCaptureStatus,
    }),
    [
      coords,
      isGeolocationAvailable,
      isGeolocationEnabled,
      positionError,
      gpsCaptureStatus,
    ]
  );

  // Validate GPS data structure
  const validateGPSData = (gpsData) => {
    if (!gpsData || typeof gpsData !== 'object') {
      return false;
    }
    
    // Check for required GPS properties
    const requiredFields = ['latitude', 'longitude'];
    const hasRequiredFields = requiredFields.every(field => {
      return typeof gpsData[field] === 'number' && !isNaN(gpsData[field]);
    });
    
    if (!hasRequiredFields) {
      console.error('❌ GPS data missing required fields:', {
        provided: Object.keys(gpsData),
        required: requiredFields,
        data: gpsData
      });
      return false;
    }
    
    return true;
  };

  // Handle GPS request from service worker
  const handleGPSRequestFromServiceWorker = useCallback((config = {}) => {    
    // Check geolocation support
    if (!('geolocation' in navigator)) {
      console.error('❌ Geolocation not supported');
      if (serviceWorkerRef.current?.active) {
        serviceWorkerRef.current.active.postMessage({
          type: 'GPS_ERROR_RESPONSE',
          error: {
            message: 'Geolocation not supported by this browser',
            code: 'GEOLOCATION_NOT_SUPPORTED',
            timestamp: Date.now()
          }
        });
      }
      return;
    }
  
    // Even if our flags say it's not available, try anyway in case permissions changed
    const options = {
      enableHighAccuracy: config.enableHighAccuracy !== false,
      timeout: config.timeout || 15000,
      maximumAge: config.maximumAge || 10000
    };
  
    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Update our state if this succeeded
        if (!isGeolocationAvailable || !isGeolocationEnabled) {
          setIsGeolocationAvailable(true);
          setIsGeolocationEnabled(true);
          setGpsCaptureStatus('GPS now available, tracking active');
        }
        
        if (serviceWorkerRef.current?.active) {
          serviceWorkerRef.current.active.postMessage({
            type: 'GPS_POSITION_RESPONSE',
            position: {
              coords: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                speed: position.coords.speed,
                heading: position.coords.heading
              },
              timestamp: position.timestamp
            }
          });
        }
      },
      (error) => {
        console.error('❌ Geolocation error:', error);
        
        // Update our state
        setIsGeolocationEnabled(false);
        
        let errorMessage = 'Unknown GPS error';
        let errorCode = 'UNKNOWN_ERROR';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'GPS permission denied by user';
            errorCode = 'PERMISSION_DENIED';
            setGpsCaptureStatus('GPS permission denied. Please enable location access.');
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'GPS position unavailable';
            errorCode = 'POSITION_UNAVAILABLE';
            setGpsCaptureStatus('GPS position unavailable. Check your location settings.');
            break;
          case error.TIMEOUT:
            errorMessage = 'GPS request timeout';
            errorCode = 'TIMEOUT';
            setGpsCaptureStatus('GPS request timeout. Retrying...');
            break;
          default:
            errorMessage = error.message || 'GPS error occurred';
            errorCode = 'GPS_ERROR';
            setGpsCaptureStatus(`GPS error: ${errorMessage}`);
        }
        
        if (serviceWorkerRef.current?.active) {
          serviceWorkerRef.current.active.postMessage({
            type: 'GPS_ERROR_RESPONSE',
            error: {
              message: errorMessage,
              code: errorCode,
              originalError: error.message,
              timestamp: Date.now()
            }
          });
        }
      },
      options
    );
  }, [isGeolocationAvailable, isGeolocationEnabled]);

  return (
    <ServiceWorkerGeoLocationContext.Provider value={contextValue}>
      {children}
    </ServiceWorkerGeoLocationContext.Provider>
  );
};

ServiceWorkerGeoLocationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

